import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, set } from '@ember/object';
import { getOwner } from '@ember/application';
import { task, timeout } from 'ember-concurrency';
import { delRelationship, addRelationship } from 'beebee-base/lib/utility';
import { createPopper } from '@popperjs/core';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{#if this.showAddNew}}\n  <div class=\'grid-x\'>\n    <div class=\'cell medium-12\'>\n      <SelectAssociation\n        @applicationController={{@applicationController}}\n        @relatedModelType={{@relatedModelType}}\n        @defaultStatusName={{@defaultStatusName}}\n        @action={{perform this.addRelatedTask}}\n        data-test-has-many-add-related\n        @addNew={{this.addNew}}\n        @wrapLabelSearchInput={{@wrapLabelSearchInput}}\n        @documentType={{@documentType}}\n        @status={{this.defStatus}}\n        @appendQuery={{@appendQuery}}\n        @filter_by_contact={{@filter_by_contact}}\n        @filter_by_product={{@filter_by_product}}\n        @filter_by_project={{@filter_by_project}}\n        @multiple={{true}}\n        @selectedIds={{this.selectedIds}}\n        @onRemove={{if this.isRemovable this.removeById}}\n      />\n    </div>\n  </div>\n{{/if}}\n\n{{! @relatedHasMany.length: the hasMany ManyArray is a STABLE reference, so\n    did-update never fires when records are pushed into it (e.g. a payment\n    saved from bb-payment-form-modal) — only on @total, which needs a server\n    round-trip. Tracking the length refreshes the chip map immediately. }}\n<div\n  class=\'{{this.chipWrapClass}}\'\n  ...attributes\n  {{did-update this.reloadMap @total @relatedHasMany @relatedHasMany.length}}\n>\n  {{! UI nuova: the cell is the .nv-recs row and each chip gets the inline unlink ✕ when removal is allowed }}\n  <div class=\'cell medium-12{{if this.isNuova \" nv-recs\"}}\'>\n    {{#each this.relatedHasManyMap as |hasManyItem|}}\n      {{#if hasManyItem.id}}\n        <BbChip\n          @applicationController={{@applicationController}}\n          @model={{@relatedModelType}}\n          @item={{hasManyItem}}\n          @openDropFn={{this.openDropTask}}\n          @onRemove={{if this.isRemovable (fn this.removeRelated hasManyItem)}}\n          @removeBusy={{this.removeRelatedTask.isRunning}}\n        />\n      {{/if}}\n    {{/each}}\n  </div>\n</div>\n\n<BbHasManyPaginator\n  @relatedModelType={{@relatedModelType}}\n  @relatedHasMany={{@relatedHasMany}}\n  @item={{@item}}\n  @status={{@status}}\n  @total={{@total}}\n  @onPage={{this.onPage}}\n  data-test-on-page\n/>");

var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6;
let HasManyComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  restartable: true
}), _dec3 = task({
  drop: true
}), _dec4 = task({
  drop: true
}), _class = class HasManyComponent extends Component {
  get isEditable() {
    return this.args.isEditable === false ? false : true;
  }

  /**
   * Whether the chip preview offers "remove relationship".
   *
   * Defaults to @isEditable, so every existing call site keeps exactly the
   * behaviour it has today. Passing @isRemovable={{true}} together with
   * @isEditable={{false}} yields a DETACH-ONLY list — no add picker: the invoice
   * payments card needs that, because linking a payment there has to keep going
   * through invoice-detail's own /pay_event sequence, while detaching one has to
   * be possible at all (POP-225: today the only way to drop a payment off an
   * invoice destroys the payment, removing it from the appointment too).
   */
  get isRemovable() {
    return this.args.isRemovable === undefined ? this.isEditable : !!this.args.isRemovable;
  }
  get saveRelation() {
    return this.args.saveRelation === true ? true : false;
  }
  get addNew() {
    return this.args.addNew === false ? false : true;
  }
  constructor() {
    super(...arguments);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "session", _descriptor3, this);
    _initializerDefineProperty(this, "notify", _descriptor4, this);
    _initializerDefineProperty(this, "relatedHasManyMap", _descriptor5, this);
    // Page published by BbHasManyPaginator through `@onPage`. `null` = no page
    // override, render the relationship as loaded. The paginator no longer
    // mutates the relationship itself (it used to, which truncated the record's
    // hasMany and got the truncation PATCHed on the next save) — this buffer is
    // now the only thing paging changes.
    // See docs/code-audit/PAGINATOR-RELATIONSHIP-DATA-LOSS.md.
    _initializerDefineProperty(this, "pagedItems", _descriptor6, this);
    this.loadRelatedHasManyMap.perform();
  }
  willDestroy() {
    super.willDestroy(...arguments);
    this.loadRelatedHasManyMap?.cancelAll?.();
    this.removeRelatedTask?.cancelAll?.();
    this.addRelatedTask?.cancelAll?.();
    if (this.args.applicationController?.prevChip?._this === this) {
      this.args.applicationController.zfprevChip?.close();
    }
    this._popperInstance?.destroy();
    this._popperInstance = null;
  }
  get defStatus() {
    const status = this.args.status;
    const optionField = this.session.option_field;
    const defStatusName = this.args.defaultStatusName;
    const relatedModelType = this.args.relatedModelType;
    if (status) return status;
    if (optionField && defStatusName && optionField.def_status?.[defStatusName]) {
      const defStatusId = optionField.def_status[defStatusName].toString();
      const defStatuses = this.session.me?.organization?.[`${relatedModelType}_states`] || [];
      return defStatuses.find(s => s.id === defStatusId) || null;
    }
    return null;
  }
  get showAddNew() {
    const isEditable = this.isEditable;
    const maxItems = this.args.maxItems;
    const length = this.relatedHasManyMap?.length ?? 0;
    return isEditable && (maxItems == null || length < maxItems);
  }

  // UI nuova combobox: the records already chosen, ticked in the list (string ids — the map holds store records)
  get selectedIds() {
    return (this.relatedHasManyMap ?? []).filter(i => i?.id).map(i => String(i.id));
  }

  // UI nuova combobox: a second click on a ticked row removes that record, after the same Sì / No confirm as the chip
  // ✕ (SelectAssociation asks, BbUnlinkConfirm); returns the TaskInstance so the confirm stays busy until it settles
  removeById(id) {
    // silent-guard-ok: the remove control is passed / rendered only when isRemovable
    if (!this.isRemovable) return;
    const item = (this.relatedHasManyMap ?? []).find(i => String(i?.id) === String(id));
    return item ? this.removeRelatedTask.perform(item) : null;
  }

  // UI nuova (optional lookup: an app without service:ui keeps the classic markup)
  get isNuova() {
    return getOwner(this)?.lookup('service:ui')?.isNuova === true;
  }
  get chipWrapClass() {
    return this.args.chipWrapClass ?? 'grid-x has-many-grid';
  }

  /**
   * True when the relationship behind this component can no longer be awaited.
   *
   * Deleting the owning record from a detail modal unloads it while this
   * component is still mounted for one more render. Ember Data then DESTROYS the
   * PromiseManyArray and nulls its internal promise, so `yield`ing it throws
   * "null is not an object (evaluating 'this.promise.then')" — and it throws
   * SYNCHRONOUSLY out of `.perform()`, i.e. outside any try/catch written inside
   * the task body (EMBER-MEDICAL-2SQ / POP-399). There is nothing to load: the
   * relationship went away with its record.
   */
  get relationshipIsGone() {
    const owner = this.args.item;
    if (owner?.isDestroyed || owner?.isDestroying) return true;
    const related = this.args.relatedHasMany;
    return Boolean(related && 'promise' in related && related.promise == null);
  }
  *loadRelatedHasManyMap() {
    if (this.relationshipIsGone) {
      this.relatedHasManyMap = [];
      return;
    }
    let arrFilter;
    if (this.pagedItems != null) {
      arrFilter = this.pagedItems;
    } else {
      try {
        arrFilter = yield this.args.relatedHasMany;
      } catch {
        // The related hasMany can legitimately reject: 403 "Non hai i diritti"
        // when the user lacks rights to read the related resource (e.g. payments),
        // or a transient network error. Degrade to an empty list instead of
        // letting the task reject as an unhandled promise.
        // (Sentry EMBER-GUIDEMONTEROSA-7N / EMBER-GUIDEALPINE-AV)
        this.relatedHasManyMap = [];
        return;
      }
    }
    if (!Array.isArray(arrFilter)) {
      this.relatedHasManyMap = [];
      return;
    }
    const statusId = this.defStatus?.id;
    const relatedModelType = this.args.relatedModelType;
    let filteredArray = arrFilter;
    if (statusId) {
      try {
        const filtered = yield Promise.all(arrFilter.map(async item => {
          const relatedState = await item.get(`${relatedModelType}_state`);
          return relatedState?.id === statusId ? item : null;
        }));
        filteredArray = filtered.filter(Boolean);
      } catch {
        filteredArray = arrFilter;
      }
    }
    const unique = Array.from(new Map(filteredArray.map(item => [item.id, item])).values());
    this.relatedHasManyMap = unique;
  }
  *openDropTask(item, chipId) {
    const refEl = document.querySelector(`#${chipId}`);
    const popEl = document.querySelector('#zfprevChip');

    // The preview popper is driven entirely through applicationController.prevChip;
    // without it, set() below would assert on undefined.
    // silent-guard-ok: positioning lifecycle, not a user refusal
    if (!popEl || !refEl || !this.args.applicationController) return;
    const prevChip = this.args.applicationController.prevChip;
    if (popEl.classList.contains('is-open') && prevChip?.relDivId === chipId) {
      this.args.applicationController.zfprevChip?.close();
      return;
    }

    // [regression-fix] model and id were present in Classic (via chip-item standalone path)
    // but dropped when openDropFn was introduced; setPreviewTask guards on prevChip.id
    // and uses prevChip.model for store.findRecord — without them preview/navigation silently no-ops
    set(this.args.applicationController, 'prevChip', {
      chipLabel: item.label,
      chipSubLabel: item.sub_label,
      chipStatus: item.status_name,
      // application.hbs gates ONLY the "remove relationship" entry on this flag.
      isEditable: this.isRemovable,
      removeRelated: this.removeRelated,
      item,
      model: this.args.relatedModelType,
      id: item.id,
      _this: this,
      relDivId: chipId
    });
    yield timeout(50);
    if (this.isDestroying || this.isDestroyed) return;
    popEl.classList.add('is-open');
    document.body.classList.add('dropdown-open');
    this._popperInstance?.destroy();
    this._popperInstance = createPopper(refEl, popEl, {
      placement: 'bottom',
      modifiers: [{
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'right', 'top', 'left']
        }
      }]
    });
  }
  reloadMap() {
    this.loadRelatedHasManyMap.perform();
  }

  // BbHasManyPaginator output channel. `items === null` restores the
  // relationship as the rendering source (page 1, no search term).
  onPage(items) {
    this.pagedItems = items;
    this.loadRelatedHasManyMap.perform();
  }

  // returns the TaskInstance: the nuova chip ✕ confirm stays busy until the unlink settles
  removeRelated(item) {
    return this.removeRelatedTask.perform(item);
  }
  *removeRelatedTask(item) {
    // silent-guard-ok: the owner record is being torn down (POP-399): its panel is closing
    if (this.relationshipIsGone) return false;
    const relatedHasMany = yield this.args.relatedHasMany;

    // Non-optimistic (house convention): confirm the server write BEFORE any
    // visible mutation or host callback. saveRelation never rejects — it
    // resolves `false` (or returns a bare `false` when an id/model name is
    // missing, e.g. a call site that forgot @relatedModelType). Because nothing
    // has been mutated yet, a refusal needs no revert: just report and bail.
    if (this.saveRelation && this.args.item?.id) {
      const detachResult = this._saveAssociation(item.id, this.args.item.id, 'del');
      const detached = yield detachResult;
      if (detached === false) {
        this._notifyBareRefusal(detachResult);
        return false;
      }
    } else {
      delRelationship(this.args.item, item);
    }

    // The write is confirmed: only now tell the host and drop the chip.
    this.args.action?.(null);
    this.args.actionRemove?.(item.id);
    if (Array.isArray(relatedHasMany)) {
      const _i = relatedHasMany.indexOf(item);
      if (_i !== -1) {
        relatedHasMany.splice(_i, 1);
      }
    }

    // A genuine association edit: mirror it into the current page buffer too,
    // otherwise the chip would survive the removal until the next page change.
    if (Array.isArray(this.pagedItems)) {
      this.pagedItems = this.pagedItems.filter(i => i !== item);
    }
    this.args.applicationController?.zfprevChip?.close();
    this.loadRelatedHasManyMap.perform();
    return true;
  }
  *addRelatedTask(id) {
    // silent-guard-ok: the owner record is being torn down (POP-399): its panel is closing
    if (this.relationshipIsGone) return false;
    const relatedHasMany = yield this.args.relatedHasMany;
    if (this._checkIsPresent(relatedHasMany, id)) {
      this.notify.warning(this.intl.t('already exist'));
      return false;
    }
    const itemFromStore = this.store.peekRecord(this.args.relatedModelType, id);
    if (!itemFromStore) return false;

    // Non-optimistic (house convention): confirm the server write BEFORE any
    // visible mutation or host callback. Same never-rejecting contract as
    // removeRelatedTask; a refusal needs no revert because nothing was mutated.
    if (this.saveRelation && this.args.item?.id) {
      const attachResult = this._saveAssociation(id, this.args.item.id, 'add');
      const attached = yield attachResult;
      if (attached === false) {
        this._notifyBareRefusal(attachResult);
        return false;
      }
    } else {
      addRelationship(this.args.item, itemFromStore);
    }

    // The write is confirmed: only now tell the host and show the chip.
    this.args.action?.(id);
    if (Array.isArray(relatedHasMany)) {
      relatedHasMany.push(itemFromStore);
    }

    // Same reasoning as removeRelatedTask: keep the visible page in sync.
    if (Array.isArray(this.pagedItems)) {
      this.pagedItems = [...this.pagedItems, itemFromStore];
    }
    this.loadRelatedHasManyMap.perform();
    return true;
  }

  /**
   * `applicationController.saveRelation` bails on its own parameter check
   * (application.js:1897 — a missing model name / id) by returning a BARE
   * `false`, before it issues any request: it never reaches `saveError`, so it
   * shows the user nothing at all and the chip just vanished a frame after they
   * clicked, indistinguishable from a UI glitch. A PROMISE that resolves `false`
   * has already reported the server's reason — notifying again would double the
   * message — so only the synchronous refusal is reported here.
   */
  _notifyBareRefusal(saveResult) {
    if (saveResult === false) {
      this.notify.error(this.intl.t('save error'));
    }
  }
  _saveAssociation(modelId, relatedModelId, operation) {
    return this.args.applicationController.saveRelation(this.args.relatedModelType, modelId, this.args.item?.constructor?.modelName, relatedModelId, operation);
  }
  _checkIsPresent(relatedHasMany, id) {
    return Array.isArray(relatedHasMany) && relatedHasMany.find(i => i.id === id);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "notify", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "relatedHasManyMap", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "pagedItems", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "removeById", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeById"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "loadRelatedHasManyMap", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "loadRelatedHasManyMap"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "openDropTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "openDropTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reloadMap", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reloadMap"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onPage", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onPage"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeRelated", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeRelated"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeRelatedTask", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "removeRelatedTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addRelatedTask", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "addRelatedTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, HasManyComponent);

export { HasManyComponent as default };
//# sourceMappingURL=has-many.js.map
